import React, { useState, useEffect } from 'react';
import API from '../../services/api';

const rs = (n) => 'Rs. ' + Number(n || 0).toLocaleString('en-PK');
const num = (n) => Number(n || 0).toLocaleString('en-PK');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '-');

const Overview = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const loadOverview = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await API.get('/v1/overview');
            if (res.data.success) setData(res.data.data);
        } catch (err) {
            console.error('Error loading overview', err);
            setError(err.response?.data?.message || 'Failed to load overview.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadOverview();
    }, []);

    const StatBox = ({ title, value, sub }) => (
        <div style={statBox}>
            <h3 style={{ margin: '0 0 5px' }}>{value}</h3>
            <p style={{ margin: 0 }}>{title}</p>
            {sub && <small style={{ color: '#64748b' }}>{sub}</small>}
        </div>
    );

    const maxMonth = data ? Math.max(...data.sales.monthly.map((m) => Number(m.amount)), 1) : 1;

    return (
        <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ color: '#1a2234', margin: 0 }}>United Motors Overview</h2>
                <button type="button" onClick={loadOverview} disabled={loading} style={refreshBtn}>
                    {loading ? 'Loading...' : 'Refresh'}
                </button>
            </div>
            <hr />

            {error && <div style={errorBox}>{error}</div>}
            {!data && loading && <p style={muted}>Loading data...</p>}

            {data && (
                <>
                    {/* ---------------- SALES ---------------- */}
                    <h3 style={sectionTitle}>Sales</h3>
                    <div style={row}>
                        <StatBox title="Total Sales" value={num(data.sales.summary.total_sales)} sub={`${num(data.sales.summary.this_month_sales)} this month`} />
                        <StatBox title="Total Revenue" value={rs(data.sales.summary.total_revenue)} sub={`${rs(data.sales.summary.this_month_revenue)} this month`} />
                        <StatBox title="Received" value={rs(data.sales.summary.total_received)} />
                        <StatBox title="Remaining" value={rs(data.sales.summary.total_remaining)} />
                    </div>

                    <div style={twoCol}>
                        <div style={panel}>
                            <h4 style={panelTitle}>Last 6 Months</h4>
                            {data.sales.monthly.length === 0 ? (
                                <p style={muted}>No sales found.</p>
                            ) : (
                                <div style={barsWrap}>
                                    {data.sales.monthly.map((m) => (
                                        <div key={m.month} style={barCol} title={`${m.count} sales`}>
                                            <small style={{ color: '#64748b', fontSize: '10px' }}>{num(m.amount)}</small>
                                            <div style={{ ...bar, height: `${(Number(m.amount) / maxMonth) * 150}px` }} />
                                            <small style={{ color: '#64748b' }}>{m.month}</small>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div style={{ ...panel, overflowX: 'auto' }}>
                            <h4 style={panelTitle}>Recent Sales</h4>
                            <table style={table}>
                                <thead>
                                    <tr>
                                        <th style={th}>Date</th>
                                        <th style={th}>Customer</th>
                                        <th style={th}>Bike</th>
                                        <th style={th}>Type</th>
                                        <th style={{ ...th, textAlign: 'right' }}>Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.sales.recent.length === 0 && (
                                        <tr><td colSpan="5" style={td}>No sales found.</td></tr>
                                    )}
                                    {data.sales.recent.map((r) => (
                                        <tr key={r.id}>
                                            <td style={td}>{fmtDate(r.sale_date)}</td>
                                            <td style={td}>{r.customer_name || '-'}</td>
                                            <td style={td}>{[r.model_name, r.version_type].filter(Boolean).join(' ') || '-'}</td>
                                            <td style={td}>
                                                <span style={badge(r.sale_type === 'Full' ? '#10b981' : '#f59e0b')}>{r.sale_type}</span>
                                            </td>
                                            <td style={{ ...td, textAlign: 'right' }}>{rs(r.amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* ---------------- BIKES ---------------- */}
                    <h3 style={sectionTitle}>Bikes</h3>
                    <div style={row}>
                        <StatBox title="Total Bikes" value={num(data.bikes.summary.total_bikes)} />
                        <StatBox title="Available" value={num(data.bikes.summary.available)} sub={`Stock value ${rs(data.bikes.summary.available_stock_value)}`} />
                        <StatBox title="Sold" value={num(data.bikes.summary.sold)} />
                        <StatBox title="Reserved" value={num(data.bikes.summary.reserved)} />
                    </div>

                    <div style={{ ...panel, overflowX: 'auto' }}>
                        <h4 style={panelTitle}>Stock by Model</h4>
                        <table style={table}>
                            <thead>
                                <tr>
                                    <th style={th}>Model</th>
                                    <th style={th}>Version</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Total</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Available</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Sold</th>
                                    <th style={th}>Sold %</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.bikes.byModel.length === 0 && (
                                    <tr><td colSpan="6" style={td}>No bikes in stock.</td></tr>
                                )}
                                {data.bikes.byModel.map((m, i) => {
                                    const pct = m.total ? Math.round((m.sold / m.total) * 100) : 0;
                                    return (
                                        <tr key={i}>
                                            <td style={td}>{m.model_name}</td>
                                            <td style={td}>{m.version_type || '-'}</td>
                                            <td style={{ ...td, textAlign: 'right' }}>{num(m.total)}</td>
                                            <td style={{ ...td, textAlign: 'right' }}>{num(m.available)}</td>
                                            <td style={{ ...td, textAlign: 'right' }}>{num(m.sold)}</td>
                                            <td style={td}>
                                                <div style={meter}><div style={{ ...meterFill, width: `${pct}%` }} /></div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* ---------------- STAFF ---------------- */}
                    <h3 style={sectionTitle}>Staff</h3>
                    <div style={row}>
                        <StatBox title="Total Staff" value={num(data.staff.summary.total_staff)} />
                        <StatBox title="Active Staff" value={num(data.staff.summary.active_staff)} />
                        <StatBox title="Inactive" value={num(data.staff.summary.inactive_staff)} />
                        <StatBox title="Monthly Salaries" value={rs(data.staff.summary.monthly_salary_total)} sub="Active staff" />
                    </div>

                    <div style={twoCol}>
                        <div style={panel}>
                            <h4 style={panelTitle}>By Designation</h4>
                            <table style={table}>
                                <tbody>
                                    {data.staff.byDesignation.length === 0 && (
                                        <tr><td style={td}>No active staff found.</td></tr>
                                    )}
                                    {data.staff.byDesignation.map((d) => (
                                        <tr key={d.designation}>
                                            <td style={td}>{d.designation}</td>
                                            <td style={{ ...td, textAlign: 'right', fontWeight: 'bold' }}>{num(d.count)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div style={{ ...panel, overflowX: 'auto' }}>
                            <h4 style={panelTitle}>Staff List</h4>
                            <table style={table}>
                                <thead>
                                    <tr>
                                        <th style={th}>Name</th>
                                        <th style={th}>Designation</th>
                                        <th style={th}>Contact</th>
                                        <th style={th}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.staff.list.map((u) => (
                                        <tr key={u.id}>
                                            <td style={td}>{u.full_name}</td>
                                            <td style={td}>{u.designation || '-'}</td>
                                            <td style={td}>{u.contact || '-'}</td>
                                            <td style={td}>
                                                <span style={badge(Number(u.status) === 1 ? '#10b981' : '#ef4444')}>
                                                    {Number(u.status) === 1 ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            <p style={{ marginTop: '20px', color: '#64748b' }}>Welcome back, Masood. System is stable.</p>
        </div>
    );
};

const cardStyle = { backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' };
const statBox = { flex: 1, minWidth: '170px', padding: '20px', backgroundColor: '#f8fafc', borderRadius: '8px', textAlign: 'center', border: '1px solid #e2e8f0' };
const row = { display: 'flex', gap: '20px', flexWrap: 'wrap', marginTop: '10px' };
const twoCol = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '20px', marginTop: '20px' };
const panel = { padding: '15px', border: '1px solid #e2e8f0', borderRadius: '8px', marginTop: '20px' };
const panelTitle = { margin: '0 0 10px', color: '#1a2234' };
const sectionTitle = { color: '#1a2234', marginTop: '30px', marginBottom: '0', borderLeft: '4px solid #2563eb', paddingLeft: '10px' };
const muted = { color: '#64748b' };
const errorBox = { background: '#fee2e2', color: '#991b1b', padding: '12px', borderRadius: '8px', marginTop: '10px' };
const refreshBtn = { padding: '10px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer' };
const table = { width: '100%', borderCollapse: 'collapse', fontSize: '14px' };
const th = { textAlign: 'left', padding: '8px', color: '#64748b', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' };
const td = { padding: '9px 8px', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' };
const badge = (bg) => ({ background: bg, color: '#fff', padding: '2px 10px', borderRadius: '99px', fontSize: '12px' });
const barsWrap = { display: 'flex', alignItems: 'flex-end', gap: '14px', height: '200px', borderBottom: '1px solid #cbd5e1' };
const barCol = { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end' };
const bar = { width: '100%', maxWidth: '50px', background: '#2563eb', borderRadius: '4px 4px 0 0', minHeight: '2px' };
const meter = { height: '8px', minWidth: '90px', background: '#e2e8f0', borderRadius: '99px', overflow: 'hidden' };
const meterFill = { height: '100%', background: '#2563eb' };

export default Overview;